import {
  SearchBarWithDropdown,
  createDebounce,
} from '@tw/tw-component-library';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { UseAuth } from '../../../../contexts/AuthContext';
import {
  searchUsers,
  UserSearchResult,
} from '../../../../apis/core/user/get/searchUsers';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.secondary || '#666'};
  margin-bottom: 1rem;
`;

const MembersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MemberTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0;
  background-color: ${({ theme }) => theme.colors.background || '#f5f5f5'};
  border-radius: 4px;
  gap: 1rem;
`;

const MemberInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.25rem;
`;

const MemberName = styled.span`
  font-size: 0.95rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text || '#333'};
`;

const MemberEmail = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.secondary || '#666'};
`;

const MemberActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AdminToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AdminCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const AdminLabel = styled.label`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text || '#333'};
  cursor: pointer;
  user-select: none;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.error || '#d32f2f'};
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.error || '#d32f2f'}15;
  }
`;

const AddButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${({ theme }) => theme.colors.primary || '#0078d4'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  align-self: flex-start;

  &:hover {
    opacity: 0.9;
  }
`;

export interface WorkspaceMember {
  id: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  email?: string;
  isOwner: boolean;
  isCreator?: boolean; // Flag to identify the creator
}

export interface WorkspaceMembersStepProps {
  userDetails?: {
    id?: string;
    firstName?: string;
    lastName?: string;
    username?: string;
    email?: string;
  } | null;
  workspaceData: {
    members: WorkspaceMember[];
  };
  onUpdate: (updates: { members: WorkspaceMember[] }) => void;
}

export const WorkspaceMembersStep: React.FC<WorkspaceMembersStepProps> = ({
  userDetails,
  workspaceData,
  onUpdate,
}) => {
  const { getAccessToken } = UseAuth();
  const { handleWarning } = useWorkspaceErrorHandler();
  const [memberInput, setMemberInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSuggestions, setLastSuggestions] = useState<UserSearchResult[]>(
    []
  );
  const lastSearchRef = useRef<string>('');
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const formatUserLabel = useCallback((suggestion: UserSearchResult) => {
    const fullName = `${suggestion.firstName || ''} ${
      suggestion.lastName || ''
    }`.trim();
    const email = suggestion.username || '';
    return email ? `${fullName} (${email})` : fullName || suggestion.id;
  }, []);

  const clearSuggestions = useCallback(() => {
    setOptions([]);
    setLastSuggestions([]);
    setIsOpen(false);
  }, []);

  const addSuggestionAsMember = useCallback(
    (match: UserSearchResult) => {
      const isAlreadyAdded = workspaceData.members.some((m) => m.id === match.id);
      const isCreator = match.id === userDetails?.id;

      if (isAlreadyAdded || isCreator) return;

      const newMember: WorkspaceMember = {
        id: match.id,
        firstName: match.firstName,
        lastName: match.lastName,
        username: match.username,
        email: match.username,
        isOwner: false,
      };
      onUpdate({ members: [...workspaceData.members, newMember] });
    },
    [onUpdate, userDetails?.id, workspaceData.members],
  );

  // Automatically add creator to members list if not already present
  useEffect(() => {
    if (userDetails?.id) {
      const creatorExists = workspaceData.members.some(
        (m) => m.id === userDetails.id
      );

      if (!creatorExists) {
        const creatorMember: WorkspaceMember = {
          id: userDetails.id,
          firstName: userDetails.firstName,
          lastName: userDetails.lastName,
          username: userDetails.username,
          email: userDetails.username || userDetails.email,
          isOwner: true, // Creator is admin by default
          isCreator: true,
        };
        onUpdate({ members: [creatorMember, ...workspaceData.members] });
      } else {
        // Ensure creator flag is set if they exist but don't have the flag
        const creatorMember = workspaceData.members.find(
          (m) => m.id === userDetails.id
        );
        if (creatorMember && !creatorMember.isCreator) {
          const updatedMembers = workspaceData.members.map((m) =>
            m.id === userDetails.id ? { ...m, isCreator: true } : m
          );
          onUpdate({ members: updatedMembers });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails?.id]);

  // Debounced user search
  const debouncedUserSearch = useCallback(
    (value: string) => {
      const debouncedFn = createDebounce(async (searchValue: string) => {
        // Skip if the search value hasn't changed
        if (lastSearchRef.current === searchValue) return;
        lastSearchRef.current = searchValue;

        if (!searchValue.trim() || searchValue.trim().length < 3) {
          if (!isMountedRef.current) return;
          setIsLoading(false);
          clearSuggestions();
          return;
        }

        try {
          setIsLoading(true);
          const response = await searchUsers({
            getAccessToken,
            searchValue: searchValue.trim(),
          });
          const users = Array.isArray(response) ? response : [];
          const suggestions = users;
          if (!isMountedRef.current) return;
          setLastSuggestions(suggestions);
          setOptions(suggestions.map(formatUserLabel));
          setIsOpen(suggestions.length > 0);
        } catch (err: unknown) {
          const normalizedError =
            err instanceof Error
              ? err
              : new Error('Failed to search users');
          console.error('Failed to search users:', normalizedError);
          if (isMountedRef.current) {
            clearSuggestions();
          }
        } finally {
          if (isMountedRef.current) {
            setIsLoading(false);
          }
        }
      }, 300);

      debouncedFn(value);
    },
    [clearSuggestions, formatUserLabel, getAccessToken],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setMemberInput(value);
      if (error) {
        setError(null);
      }

      if (!value.trim()) {
        setIsLoading(false);
        clearSuggestions();
        return;
      }

      debouncedUserSearch(value);
    },
    [clearSuggestions, debouncedUserSearch, error],
  );

  const handleSelect = useCallback(
    (selectedLabel: string | null) => {
      if (!selectedLabel) {
        setMemberInput('');
        setIsOpen(false);
        setError(null);
        return;
      }

      const match = lastSuggestions.find((suggestion) => {
        return formatUserLabel(suggestion) === selectedLabel;
      });

      if (match) {
        addSuggestionAsMember(match);
        setMemberInput('');
        setError(null);
      }

      // Close dropdown after selection
      clearSuggestions();
    },
    [addSuggestionAsMember, clearSuggestions, formatUserLabel, lastSuggestions],
  );

  const handleAddMember = useCallback(() => {
    const trimmed = memberInput.trim();
    if (!trimmed) {
      return;
    }

    const match = lastSuggestions.find((suggestion) => {
      return formatUserLabel(suggestion) === trimmed;
    });

    if (!match) {
      const warning = 'Select a user from the suggestions list.';
      setError(warning);
      handleWarning(warning);
      return;
    }

    addSuggestionAsMember(match);
    setMemberInput('');
    clearSuggestions();
    setError(null);
  }, [
    addSuggestionAsMember,
    clearSuggestions,
    formatUserLabel,
    handleWarning,
    lastSuggestions,
    memberInput,
  ]);

  const handleRemoveMember = (memberId: string) => {
    // Prevent removing the creator
    const member = workspaceData.members.find((m) => m.id === memberId);
    if (member?.isCreator) {
      return; // Don't allow removing the creator
    }
    onUpdate({
      members: workspaceData.members.filter((m) => m.id !== memberId),
    });
  };

  const handleToggleAdmin = (memberId: string) => {
    onUpdate({
      members: workspaceData.members.map((m) =>
        m.id === memberId ? { ...m, isOwner: !m.isOwner } : m
      ),
    });
  };

  const getFullName = (member: WorkspaceMember): string => {
    const firstName = member.firstName || '';
    const lastName = member.lastName || '';
    return `${firstName} ${lastName}`.trim() || member.username || member.id;
  };

  return (
    <Container data-tour="create-ws-members-step">
      <div>
        <Title>Add Members</Title>
        <Description>Add team members to your workspace (optional)</Description>
      </div>

      <SearchBarWithDropdown
        label="Member Email or ID"
        options={options}
        placeholder="Enter member email or ID (min 3 characters)"
        onSelect={handleSelect}
        onSearch={handleSearch}
        searchButton={false}
        value={memberInput}
        isOpenOverride={isOpen}
        setIsOpenOverride={setIsOpen}
        isLoading={isLoading}
        getDisplayText={(option: string) => option || ''}
        error={error ? error : undefined}
      />
      <AddButton onClick={handleAddMember}>Add</AddButton>

      {workspaceData.members.length > 0 && (
        <MembersList>
          {workspaceData.members.map((member) => {
            const isCreator = member.isCreator || member.id === userDetails?.id;
            return (
              <MemberTag key={member.id}>
                <MemberInfo>
                  <MemberName>
                    {getFullName(member)}
                    {isCreator && (
                      <span
                        style={{
                          marginLeft: '0.5rem',
                          fontSize: '0.8rem',
                          color: '#666',
                          fontStyle: 'italic',
                        }}
                      >
                        (Creator)
                      </span>
                    )}
                  </MemberName>
                  <MemberEmail>
                    {member.email || member.username || ''}
                  </MemberEmail>
                </MemberInfo>
                <MemberActions>
                  <AdminToggle>
                    <AdminCheckbox
                      type="checkbox"
                      checked={member.isOwner}
                      onChange={() => handleToggleAdmin(member.id)}
                      id={`admin-${member.id}`}
                    />
                    <AdminLabel htmlFor={`admin-${member.id}`}>
                      Owner
                    </AdminLabel>
                  </AdminToggle>
                  {!isCreator && (
                    <RemoveButton onClick={() => handleRemoveMember(member.id)}>
                      Remove
                    </RemoveButton>
                  )}
                </MemberActions>
              </MemberTag>
            );
          })}
        </MembersList>
      )}
    </Container>
  );
};
